import { useState, useEffect, useRef, useCallback } from 'react';
import useSWR from 'swr';
import {
  Box,
  Text,
  Group,
  Stack,
  ScrollArea,
  TextInput,
  Textarea,
  ActionIcon,
  Center,
  Loader,
  UnstyledButton,
} from '@mantine/core';
import { IconMessage, IconSearch, IconSend, IconPhone } from '@tabler/icons-react';
import { smsApi } from '../api/client';
import { useAuth } from '../context/AuthContext';
import type { SmsMessageResponse } from '../types';

// ─── Design tokens ────────────────────────────────────────────────────────────
const BG = '#0C0C0E';
const CARD_BG = '#18181B';
const BORDER = '#27272A';
const TEXT_PRIMARY = '#F4F4F5';
const TEXT_SECONDARY = '#A1A1AA';
const TEXT_MUTED = '#71717A';
const BLUE = '#2563EB';
const GREEN = '#22C55E';

interface SmsThread {
  phone: string;
  name: string;
  last_message: string;
  last_message_at: string;
  message_count: number;
  conversation_id?: number;
  direction?: string;
}

function formatRelativeTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDateGroup(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function SmsInboxPage() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [composeText, setComposeText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch threads with SWR (10s refresh)
  const { data: threadsData, isLoading: threadsLoading, mutate: mutateThreads } = useSWR(
    ['sms-threads', debouncedSearch],
    () => smsApi.threads({ search: debouncedSearch || undefined, limit: 100 }),
    { refreshInterval: 10000 },
  );

  const threads: SmsThread[] = threadsData?.threads ?? [];

  // Fetch messages for selected thread with SWR (5s refresh)
  const { data: messages, mutate: mutateMessages } = useSWR<SmsMessageResponse[]>(
    selectedPhone ? ['sms-messages', selectedPhone] : null,
    () => smsApi.list({ phone: selectedPhone!, limit: 200 }),
    { refreshInterval: 5000 },
  );

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Send message
  const handleSend = useCallback(async () => {
    if (!selectedPhone || !composeText.trim() || sending) return;
    setSending(true);
    try {
      await smsApi.send({ to: selectedPhone, body: composeText.trim() });
      setComposeText('');
      mutateMessages();
      mutateThreads();
    } catch (e) {
      console.error('[SMS_INBOX] send failed:', e);
    } finally {
      setSending(false);
    }
  }, [selectedPhone, composeText, sending, mutateMessages, mutateThreads]);

  // Handle Enter key in textarea
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  // Group messages by date
  const messageGroups: { date: string; items: SmsMessageResponse[] }[] = [];
  if (messages) {
    for (const msg of messages) {
      const dateStr = formatDateGroup(msg.created_at);
      const last = messageGroups[messageGroups.length - 1];
      if (last?.date === dateStr) {
        last.items.push(msg);
      } else {
        messageGroups.push({ date: dateStr, items: [msg] });
      }
    }
  }

  const selectedThread = threads.find((t) => t.phone === selectedPhone);

  return (
    <Box style={{ display: 'flex', height: '100%', background: BG }}>
      {/* ── Left Panel: Thread List ── */}
      <Box
        style={{
          width: 280,
          flexShrink: 0,
          borderRight: `1px solid ${BORDER}`,
          display: 'flex',
          flexDirection: 'column',
          background: CARD_BG,
        }}
      >
        {/* Header */}
        <Box style={{ padding: '16px 16px 12px', borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
          <Group gap={8} mb={12}>
            <IconMessage size={18} color={GREEN} />
            <Text size="sm" fw={700} c={TEXT_PRIMARY}>
              SMS Inbox
            </Text>
          </Group>
          <TextInput
            placeholder="Search name or phone..."
            leftSection={<IconSearch size={13} color="#52525B" />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            size="xs"
            styles={{
              input: {
                background: 'transparent',
                border: `1px solid ${BORDER}`,
                color: TEXT_PRIMARY,
                fontSize: 12,
              },
            }}
          />
        </Box>

        {/* Thread List */}
        <ScrollArea style={{ flex: 1 }} scrollbarSize={4}>
          {threadsLoading ? (
            <Center h={200}>
              <Loader size={16} color="gray" />
            </Center>
          ) : threads.length === 0 ? (
            <Center h={200}>
              <Stack align="center" gap={4}>
                <IconMessage size={24} color={TEXT_MUTED} />
                <Text c={TEXT_MUTED} size="sm">
                  No conversations
                </Text>
              </Stack>
            </Center>
          ) : (
            threads.map((thread) => {
              const isSelected = selectedPhone === thread.phone;
              const isInbound = thread.direction === 'inbound';
              return (
                <UnstyledButton
                  key={thread.phone}
                  onClick={() => setSelectedPhone(thread.phone)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '12px 16px',
                    borderLeft: isSelected ? `3px solid ${BLUE}` : '3px solid transparent',
                    background: isSelected ? 'rgba(37,99,235,0.08)' : 'transparent',
                    transition: 'background 0.12s',
                    borderBottom: `1px solid ${BORDER}`,
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.03)';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'transparent';
                  }}
                >
                  {/* Unread indicator for inbound */}
                  <Box style={{ width: 8, paddingTop: 6, flexShrink: 0 }}>
                    {isInbound && (
                      <Box
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          background: BLUE,
                        }}
                      />
                    )}
                  </Box>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Group gap={6} justify="space-between" wrap="nowrap" mb={2}>
                      <Text
                        size="xs"
                        fw={isInbound ? 700 : 500}
                        c={TEXT_PRIMARY}
                        truncate
                        style={{ flex: 1, minWidth: 0 }}
                      >
                        {thread.name || thread.phone}
                      </Text>
                      <Text size="10px" c={TEXT_MUTED} style={{ flexShrink: 0 }}>
                        {formatRelativeTime(thread.last_message_at)}
                      </Text>
                    </Group>
                    {thread.name && (
                      <Text size="10px" c={TEXT_MUTED} mb={2}>
                        {thread.phone}
                      </Text>
                    )}
                    <Text size="11px" c={TEXT_SECONDARY} truncate lineClamp={1}>
                      {thread.last_message}
                    </Text>
                  </Box>
                </UnstyledButton>
              );
            })
          )}
        </ScrollArea>
      </Box>

      {/* ── Right Panel: Messages ── */}
      <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {!selectedPhone ? (
          /* No thread selected */
          <Center style={{ flex: 1 }}>
            <Stack align="center" gap={8}>
              <IconMessage size={40} color={TEXT_MUTED} style={{ opacity: 0.5 }} />
              <Text c={TEXT_MUTED} size="sm">
                Select a conversation to view messages
              </Text>
            </Stack>
          </Center>
        ) : (
          <>
            {/* Thread Header */}
            <Box
              style={{
                padding: '14px 20px',
                borderBottom: `1px solid ${BORDER}`,
                background: CARD_BG,
                flexShrink: 0,
              }}
            >
              <Group gap={10}>
                <Box
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: 'rgba(37,99,235,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <IconPhone size={16} color={BLUE} />
                </Box>
                <Box>
                  <Text size="sm" fw={600} c={TEXT_PRIMARY}>
                    {selectedThread?.name || selectedPhone}
                  </Text>
                  <Text size="11px" c={TEXT_MUTED}>
                    {selectedPhone}
                    {selectedThread?.message_count
                      ? ` \u00B7 ${selectedThread.message_count} messages`
                      : ''}
                  </Text>
                </Box>
              </Group>
            </Box>

            {/* Messages Area */}
            <ScrollArea style={{ flex: 1 }} scrollbarSize={4} viewportRef={scrollAreaRef}>
              <Box style={{ padding: '16px 20px' }}>
                {!messages ? (
                  <Center h={200}>
                    <Loader size={16} color="gray" />
                  </Center>
                ) : messages.length === 0 ? (
                  <Center h={200}>
                    <Stack align="center" gap={4}>
                      <IconMessage size={24} color={TEXT_MUTED} />
                      <Text c={TEXT_MUTED} size="sm">
                        No messages yet
                      </Text>
                    </Stack>
                  </Center>
                ) : (
                  <Stack gap={0}>
                    {messageGroups.map((group) => (
                      <Box key={group.date}>
                        {/* Date separator */}
                        <Box
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            padding: '16px 0 8px',
                          }}
                        >
                          <Box style={{ flex: 1, height: 1, background: '#222' }} />
                          <Text
                            size="10px"
                            fw={700}
                            c={TEXT_MUTED}
                            tt="uppercase"
                            style={{ letterSpacing: '0.08em', flexShrink: 0 }}
                          >
                            {group.date}
                          </Text>
                          <Box style={{ flex: 1, height: 1, background: '#222' }} />
                        </Box>

                        {/* Messages */}
                        {group.items.map((sms) => {
                          const isOut = sms.direction === 'outbound';
                          const employee = (sms as any).message_metadata?.employee;
                          const time = formatTime(sms.created_at);
                          return (
                            <Box key={sms.id} style={{ padding: '6px 0' }}>
                              <Group gap={6} mb={3}>
                                <Box
                                  style={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    background: isOut ? GREEN : '#3B82F6',
                                    flexShrink: 0,
                                  }}
                                />
                                <Text size="11px" fw={600} c={isOut ? GREEN : '#60A5FA'}>
                                  {isOut ? employee || 'Shop' : selectedThread?.name || 'Customer'}
                                </Text>
                                <Text size="10px" c={TEXT_MUTED}>
                                  {time}
                                </Text>
                              </Group>
                              <Box
                                style={{
                                  marginLeft: 12,
                                  padding: '10px 14px',
                                  background: isOut
                                    ? 'rgba(34,197,94,0.12)'
                                    : 'rgba(59,130,246,0.10)',
                                  borderLeft: `3px solid ${isOut ? 'rgba(34,197,94,0.5)' : 'rgba(59,130,246,0.4)'}`,
                                  borderRadius: '0 8px 8px 0',
                                }}
                              >
                                <Text
                                  size="13px"
                                  c={TEXT_SECONDARY}
                                  style={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    lineHeight: 1.6,
                                  }}
                                >
                                  {sms.body}
                                </Text>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    ))}
                    <div ref={messagesEndRef} />
                  </Stack>
                )}
              </Box>
            </ScrollArea>

            {/* Compose Area */}
            <Box
              style={{
                padding: '12px 20px 16px',
                borderTop: `1px solid ${BORDER}`,
                background: CARD_BG,
                flexShrink: 0,
              }}
            >
              <Group gap={8} align="flex-end">
                <Textarea
                  placeholder="Type a message..."
                  value={composeText}
                  onChange={(e) => setComposeText(e.currentTarget.value)}
                  onKeyDown={handleKeyDown}
                  autosize
                  minRows={1}
                  maxRows={4}
                  style={{ flex: 1 }}
                  styles={{
                    input: {
                      background: 'transparent',
                      border: `1px solid ${BORDER}`,
                      color: TEXT_PRIMARY,
                      fontSize: 13,
                      '&:focus': {
                        borderColor: BLUE,
                      },
                    },
                  }}
                />
                <ActionIcon
                  size={36}
                  variant="filled"
                  color="blue"
                  onClick={handleSend}
                  loading={sending}
                  disabled={!composeText.trim()}
                  style={{
                    borderRadius: 8,
                    opacity: composeText.trim() ? 1 : 0.5,
                  }}
                >
                  <IconSend size={16} />
                </ActionIcon>
              </Group>
              <Text size="xs" c={TEXT_MUTED} mt={4} style={{ opacity: 0.6 }}>
                {composeText.length} / 1,600 characters
              </Text>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
